import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import DashboardNavigation from './DashboardNavigation'
import Footer from './Footer'
import LoadingIcon from './LoadingIcon'
import { ChevronLeftIcon } from './ui'
import { useApi } from '../services/api'
import { useScrollToTopOnErrorSignal } from '../hooks/useScrollToTopOnErrorSignal'

type FlowStep = 'loading' | 'widget' | 'success' | 'error'

/** Paydock HtmlWidget paymentType — from URL ?method=card | direct_debit */
type PaydockWidgetPaymentType = 'card' | 'bank_account'

function widgetAccessToken(): string | undefined {
  const t = import.meta.env.VITE_PAYDOCK_WIDGET_ACCESS_TOKEN as string | undefined
  return t?.trim() || undefined
}

/**
 * Paydock API host must match the widget access token.
 * Defaults to sandbox (typical for public widget keys); set VITE_PAYDOCK_WIDGET_ENV=production for live Paydock.
 */
function resolvePaydockWidgetEnv(): 'sandbox' | 'production' {
  const v = (import.meta.env.VITE_PAYDOCK_WIDGET_ENV as string | undefined)?.trim().toLowerCase()
  if (v === 'production') return 'production'
  if (v === 'sandbox') return 'sandbox'
  return 'sandbox'
}

function extractFinishToken(paymentSource: unknown): string | null {
  if (typeof paymentSource === 'string' && paymentSource.trim()) return paymentSource.trim()
  if (paymentSource && typeof paymentSource === 'object' && 'token' in paymentSource) {
    const t = (paymentSource as { token?: unknown }).token
    if (typeof t === 'string' && t.trim()) return t.trim()
  }
  return null
}

function paydockWidgetPaymentTypeFromParams(params: URLSearchParams): PaydockWidgetPaymentType {
  const m = params.get('method')?.trim().toLowerCase()
  if (m === 'direct_debit' || m === 'bank' || m === 'bank_account') return 'bank_account'
  return 'card'
}

export default function ChangePaymentMethodView() {
  const navigate = useNavigate()
  const { policyId } = useParams()
  const [searchParams] = useSearchParams()
  const paydockPaymentType = paydockWidgetPaymentTypeFromParams(searchParams)
  const api = useApi()
  const apiRef = useRef(api)
  apiRef.current = api
  const csrfRef = useRef<string | null>(null)

  const [step, setStep] = useState<FlowStep>('loading')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  useScrollToTopOnErrorSignal(errorMessage)
  const [formValid, setFormValid] = useState(false)
  const [widgetBusy, setWidgetBusy] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const widgetRef = useRef<{ destroy?: () => void; trigger?: (ev: string) => void } | null>(null)

  const backToPayment = () => {
    if (policyId) {
      navigate(`/payment/${encodeURIComponent(policyId)}`, { state: { preserveScroll: true } })
    } else {
      navigate('/dashboard')
    }
  }

  const fail = useCallback((msg: string) => {
    setErrorMessage(msg)
    setStep('error')
  }, [])

  useEffect(() => {
    if (!policyId) {
      fail('Missing policy.')
      return
    }

    let cancelled = false
    const ac = new AbortController()
    ;(async () => {
      try {
        const start = await apiRef.current.startPaymentMethodSession(policyId, { signal: ac.signal })
        if (cancelled) return
        if (!start.success || !start.data) {
          fail(start.error || 'Could not start payment update. Please try again.')
          return
        }
        csrfRef.current = start.data.csrfToken ?? null
        setStep('widget')
      } catch (e) {
        if (cancelled || ac.signal.aborted) return
        if (!cancelled) {
          fail(e instanceof Error ? e.message : 'Something went wrong. Please try again.')
        }
      }
    })()

    return () => {
      cancelled = true
      ac.abort()
    }
  }, [fail, policyId])

  useEffect(() => {
    if (step !== 'widget' || !policyId || !widgetAccessToken()) return

    let cancelled = false
    const containerId = 'paydock-widget-container'
    const paymentType = paydockPaymentType
    const widgetPurpose =
      paymentType === 'bank_account' ? 'payment_source' : 'card_payment_source_with_cvv'

    const init = async () => {
      setWidgetBusy(true)
      setFormValid(false)
      try {
        const mod = await import('@paydock/client-sdk')
        if (cancelled) return
        const HtmlWidgetConstructor = (mod as { HtmlWidget?: new (...args: unknown[]) => unknown }).HtmlWidget
        if (!HtmlWidgetConstructor) {
          fail('Paydock widget is not available.')
          return
        }

        const accessToken = widgetAccessToken()
        if (!accessToken) {
          fail('Paydock widget is not available.')
          return
        }

        const el = document.getElementById(containerId)
        if (el) el.innerHTML = ''

        const widgetEnv = resolvePaydockWidgetEnv()
        const widget = new HtmlWidgetConstructor(
          `#${containerId}`,
          accessToken,
          'not_configured',
          paymentType,
          widgetPurpose
        ) as {
          destroy?: () => void
          trigger?: (ev: string) => void
          setFormElements?: (els: unknown[]) => void
          setStyles?: (s: Record<string, string>) => void
          setElementStyle?: (el: string, s: Record<string, string>) => void
          setSupportedCardIcons?: (icons: string[], v: boolean) => void
          setEnv?: (e: string) => void
          useAutoResize?: () => void
          setHiddenElements?: (els: string[]) => void
          setTexts?: (t: Record<string, string>) => void
          on?: (ev: string, fn: (...args: unknown[]) => void) => void
          load?: () => void
        }

        if (paymentType === 'bank_account') {
          widget.setFormElements?.([
            { field: 'account_name*', label: 'Account name *', placeholder: 'Name on the account' },
            { field: 'account_bsb*', label: 'BSB *', placeholder: '000-000' },
            { field: 'account_number*', label: 'Account number *', placeholder: 'Account number' },
          ])
        } else {
          widget.setFormElements?.([
            { field: 'card_name*', label: 'Card Holders Name *', placeholder: 'Card Holders Name' },
            { field: 'card_number*', label: 'Card Number *', placeholder: 'Card Number' },
            { field: 'card_ccv*', label: 'Security Code *', placeholder: 'Security Code' },
            { field: 'expire_month*', label: 'Expiry month *', placeholder: 'MM/YY' },
            { field: 'expire_year*', label: 'Expiry year *', placeholder: 'YY' },
          ])
        }
        widget.setStyles?.({
          background_color: '#ffffff',
          border_color: '#e5e7eb',
          text_color: '#111827',
        })
        widget.setElementStyle?.('input', {
          border_radius: '8px',
          font_size: '16px',
          font_weight: '400',
        })
        if (paymentType === 'card') {
          widget.setSupportedCardIcons?.(['mastercard', 'visa'], true)
        }
        widget.setEnv?.(widgetEnv)
        widget.useAutoResize?.()
        widget.setHiddenElements?.(['submit_button'])
        widget.setTexts?.({ finish_text: '' })

        widget.on?.('afterLoad', () => {
          if (!cancelled) setWidgetBusy(false)
        })
        widget.on?.('validation', (data: unknown) => {
          const d = data as { form_valid?: boolean }
          if (!cancelled) {
            const ok = Boolean(d?.form_valid)
            setFormValid(ok)
            if (!ok) setSubmitting(false)
          }
        })
        widget.on?.('finish', (data: unknown) => {
          const d = data as { payment_source?: unknown }
          const token = extractFinishToken(d?.payment_source)
          if (!token || !policyId) return
          void (async () => {
            setSubmitting(true)
            setErrorMessage(null)
            try {
              const res = await apiRef.current.updatePaymentMethodVault(policyId, { token }, csrfRef.current)
              if (!res.success) {
                setErrorMessage(
                  'Something went wrong. If this issue persists, please call us on 1300 296 296.'
                )
                setSubmitting(false)
                return
              }
              setStep('success')
            } catch {
              setErrorMessage(
                'Something went wrong. If this issue persists, please call us on 1300 296 296.'
              )
            } finally {
              setSubmitting(false)
            }
          })()
        })
        widget.on?.('error', () => {
          void init()
        })

        widget.load?.()
        widgetRef.current = widget
      } catch {
        if (!cancelled) fail('Could not load payment form.')
      }
    }

    void init()

    return () => {
      cancelled = true
      widgetRef.current?.destroy?.()
      widgetRef.current = null
      const el = document.getElementById(containerId)
      if (el) el.innerHTML = ''
    }
  }, [fail, policyId, step, paydockPaymentType])

  const submitWidget = () => {
    setSubmitting(true)
    widgetRef.current?.trigger?.('submit_form')
  }

  return (
    <div className="bg-background-brand-primary flex flex-col items-start relative min-h-screen w-full">
      <DashboardNavigation />

      <main id="main" className="flex w-full flex-col items-start flex-1">
      <div className="bg-background-brand-solid flex flex-col gap-0 items-center overflow-clip px-0 py-[32px] relative shrink-0 w-full">
        <div className="flex flex-col gap-0 items-center md:items-start max-w-container-desktop px-[16px] md:px-[32px] py-0 relative shrink-0 w-full">
          <button
            type="button"
            onClick={backToPayment}
            className="cursor-pointer flex gap-[4px] items-center justify-center overflow-clip p-0 relative shrink-0"
          >
            <div className="overflow-clip relative shrink-0 size-5 text-text-primary-on-brand">
              <ChevronLeftIcon />
            </div>
            <p className="font-body font-semibold text-sm leading-sm text-text-primary-on-brand">
              Back to payment
            </p>
          </button>
          <h1 className="font-display font-medium text-display-xs leading-[32px] text-text-primary-on-brand mt-[24px] max-w-[768px]">
            Update payment method
          </h1>
          <p className="font-body font-regular text-md text-text-primary-on-brand mt-[12px] max-w-[768px] opacity-90">
            {paydockPaymentType === 'bank_account'
              ? 'Enter your bank account details in the secure form for direct debit.'
              : 'Enter your new card details in the secure form.'}
          </p>
        </div>
      </div>

      <div className="flex flex-col flex-1 w-full max-w-container-desktop px-[16px] md:px-[32px] py-[32px] mx-auto gap-[24px]">
        {step === 'loading' && (
          <div className="flex items-center gap-2 text-text-secondary-700">
            <LoadingIcon />
            <span className="font-body text-md">Loading…</span>
          </div>
        )}

        {step === 'error' && errorMessage && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-900 font-body text-sm">
            {errorMessage}
          </div>
        )}

        {step === 'widget' && (
          <div className="w-full flex justify-center">
            <div className="flex flex-col gap-4 w-full max-w-lg items-center">
              {!widgetAccessToken() && (
                <p className="text-amber-800 font-body text-sm w-full text-center">
                  Missing <code className="text-xs">VITE_PAYDOCK_WIDGET_ACCESS_TOKEN</code> — add it to your env and
                  rebuild the portal.
                </p>
              )}
              {widgetAccessToken() && (
                <>
                  <div id="paydock-widget-container" className="min-h-[120px] w-full flex justify-center" />
                  {widgetBusy && (
                    <div className="flex items-center gap-2 text-text-secondary-700 justify-center w-full">
                      <LoadingIcon />
                      <span className="font-body text-sm">Loading secure form…</span>
                    </div>
                  )}
                  {errorMessage && (
                    <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-red-900 font-body text-sm w-full">
                      {errorMessage}
                    </div>
                  )}
                  <button
                    type="button"
                    className="rounded-lg bg-background-brand-solid text-text-primary-on-brand px-4 py-2 font-body font-semibold text-sm w-fit disabled:opacity-50"
                    disabled={!formValid || widgetBusy || submitting}
                    onClick={submitWidget}
                  >
                    {submitting
                      ? 'Processing…'
                      : paydockPaymentType === 'bank_account'
                        ? 'Save direct debit'
                        : 'Update card'}
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {step === 'success' && (
          <div className="flex flex-col gap-4">
            <p className="font-body text-md text-text-secondary-700">Your payment method was updated.</p>
            <button
              type="button"
              className="rounded-lg bg-background-brand-solid text-text-primary-on-brand px-4 py-2 font-body font-semibold text-sm w-fit"
              onClick={backToPayment}
            >
              Back to payment
            </button>
          </div>
        )}
      </div>
      </main>
      <Footer />
    </div>
  )
}
